import React, { useState, useEffect } from 'react';
import syncService from '../services/syncService';

const NetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncMessage, setSyncMessage] = useState('');

  useEffect(() => {
    // Initial load
    loadPendingCount();

    // Subscribe to sync events
    const unsubscribe = syncService.subscribe((event) => {
      switch (event.type) {
        case 'online':
          setIsOnline(true);
          setSyncMessage('Back online! Syncing...');
          setTimeout(() => setSyncMessage(''), 3000);
          break;
        case 'offline':
          setIsOnline(false);
          setSyncMessage('You are offline. Changes will be saved locally.');
          break;
        case 'queued':
          setPendingCount(event.pendingCount);
          break;
        case 'syncStart':
          setIsSyncing(true);
          setSyncMessage('Syncing changes...');
          break;
        case 'syncComplete':
          setIsSyncing(false);
          setPendingCount(event.remaining);
          setLastSync(new Date());
          if (event.synced > 0) {
            setSyncMessage(`✅ ${event.synced} item${event.synced > 1 ? 's' : ''} synced`);
          }
          setTimeout(() => setSyncMessage(''), 3000);
          break;
        case 'syncError':
          setIsSyncing(false);
          setSyncMessage(`⚠️ Sync error: ${event.error}`);
          break;
        default:
          break;
      }
    });

    // Listen for online/offline
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic check for pending items
    const interval = setInterval(loadPendingCount, 10000);

    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const loadPendingCount = async () => {
    const count = await syncService.getPendingCount();
    setPendingCount(count);
  };

  const handleForceSync = async () => {
    if (!isOnline) {
      setSyncMessage('Cannot sync while offline');
      setTimeout(() => setSyncMessage(''), 2000);
      return;
    }
    setIsSyncing(true);
    await syncService.forcSync();
  };

  const formatLastSync = () => {
    if (!lastSync) return 'Never';
    const diff = Date.now() - lastSync.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    return lastSync.toLocaleTimeString();
  };

  return (
    <>
      {/* Fixed Status Bar */}
      <div 
        className={`fixed bottom-4 left-4 z-50 transition-all duration-300 ${
          showDetails ? 'w-80' : 'w-auto'
        }`}
      >
        {/* Main Status Button */}
        <button
          onClick={() => setShowDetails(!showDetails)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg transition-all ${
            !isOnline 
              ? 'bg-red-500 text-white animate-pulse' 
              : pendingCount > 0 
                ? 'bg-amber-500 text-white' 
                : 'bg-green-500 text-white'
          }`}
        >
          {/* Status Icon */}
          <span className="text-lg">
            {!isOnline ? '📴' : isSyncing ? '🔄' : pendingCount > 0 ? '⏳' : '🌐'}
          </span>
          
          {/* Status Text */}
          <span className="font-medium text-sm">
            {!isOnline 
              ? 'Offline' 
              : isSyncing 
                ? 'Syncing...' 
                : pendingCount > 0 
                  ? `${pendingCount} pending` 
                  : 'Online'
            }
          </span>

          {/* Sync indicator animation */}
          {isSyncing && (
            <span className="animate-spin">⚡</span>
          )}
        </button>

        {/* Expanded Details Panel */}
        {showDetails && (
          <div className="mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className={`px-4 py-3 ${isOnline ? 'bg-green-500' : 'bg-red-500'} text-white`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold">
                  {isOnline ? '🌐 Connected' : '📴 Offline Mode'}
                </span>
                <button 
                  onClick={() => setShowDetails(false)}
                  className="text-white/80 hover:text-white"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3">
              {/* Sync Status */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Pending Changes:</span>
                <span className={`font-bold ${pendingCount > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                  {pendingCount}
                </span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Last Sync:</span>
                <span className="text-gray-800">{formatLastSync()}</span>
              </div>

              {/* Sync Message */}
              {syncMessage && (
                <div className={`text-sm p-2 rounded-lg ${
                  syncMessage.includes('✅') ? 'bg-green-50 text-green-700' :
                  syncMessage.includes('⚠️') ? 'bg-red-50 text-red-700' :
                  'bg-blue-50 text-blue-700'
                }`}>
                  {syncMessage}
                </div>
              )}

              {/* Actions */}
              <div className="pt-2 border-t space-y-2">
                {isOnline && pendingCount > 0 && (
                  <button
                    onClick={handleForceSync}
                    disabled={isSyncing}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isSyncing ? (
                      <>
                        <span className="animate-spin">⚡</span>
                        Syncing...
                      </>
                    ) : (
                      <>
                        🔄 Sync Now
                      </>
                    )}
                  </button>
                )}

                {!isOnline && (
                  <div className="text-center text-sm text-gray-500 py-2">
                    <p>📴 Working offline</p>
                    <p className="text-xs mt-1">Your changes are saved locally and will sync when you're back online.</p>
                  </div>
                )}

                {isOnline && pendingCount === 0 && (
                  <div className="text-center text-sm text-green-600 py-2">
                    ✅ All changes synced!
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Offline Banner (shown when offline) */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white py-2 px-4 text-center text-sm font-medium z-50 shadow-md">
          <span className="mr-2">📴</span>
          You're working offline. Changes will be saved and synced when you're back online.
        </div>
      )}
    </>
  );
};

export default NetworkStatus;
